import { useEffect, useState } from 'react'

import { Box, Grid, Text, useBreakpoint } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { AvailableModules } from 'generated/featureDefinitions'
import { ProductUpdatesPoliciesDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import benefitsVideo from 'modules/product_updates/lib/assets/video-benefits.mp4'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

import CheckIcon from './_CheckIcon'

const NewLabelStyle = {
  top: '16px',
  left: '16px',
  zIndex: '1',
}

const NewBannerStyle = {
  position: 'relative' as const,
  overflow: 'hidden' as const,
  minHeight: '280px',
  maxHeight: '320px',
}

const BenefitsBanner = () => {
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false)
  const { data: upsellData } = useQuery(ProductUpdatesPoliciesDocument)
  const { breakpoint } = useBreakpoint()
  const canSeeUpsellButton = upsellData?.productUpdates._policies.upsellButtonSeeUpsellButton.can
  const isLargeScreen = breakpoint === 'xl' || breakpoint === 'xxl'

  useEffect(() => {
    if (!canSeeUpsellButton) return
    track(trackingEvents.productUpdates.viewProductUpdatesFeaturedBanner, {
      module: 'benefits',
      path: 'product_updates',
    })
  }, [canSeeUpsellButton])

  const benefits = [
    i18n.t('product_updates.updates_with_ceo.landing.banner.perks.01'),
    i18n.t('product_updates.updates_with_ceo.landing.banner.perks.02'),
    i18n.t('product_updates.updates_with_ceo.landing.banner.perks.03'),
  ]

  if (!canSeeUpsellButton) return null

  const renderUpsellModal = () => {
    if (!isUpsellModalOpen) return null

    return (
      <RequestUpgradeModal
        requestReason={ValidReasons.MissingAddon}
        moduleRequested={AvailableModules.benefits}
        requestSource="product_updates_benefits_banner"
        title={i18n.t('product_updates.updates_with_ceo.landing.upsell_modal.title')}
        description={
          <Box gap="s16">
            <Text.Normal size="300" overflowWrap>
              {i18n.t('product_updates.updates_with_ceo.landing.upsell_modal.description')}
            </Text.Normal>
          </Box>
        }
        onClose={() => {
          setIsUpsellModalOpen(false)
        }}
      />
    )
  }

  const handleUpsellButtonClick = () => {
    track(trackingEvents.productUpdates.clickProductUpdatesBenefitsBanner)
    setIsUpsellModalOpen(true)
  }

  return (
    <Box
      borderRadius={{
        all: 'abs016',
      }}
      padding="s16"
      border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
      style={{
        marginBottom: '60px',
        marginTop: '60px',
        boxShadow: '0px 2px 20px 0px rgba(13, 22, 37, 0.04)',
      }}
    >
      <Grid variant="fixed">
        <Box
          style={NewBannerStyle}
          borderRadius={{
            all: 'abs012',
          }}
          alignItems="center"
          justifyContent="center"
          gridColSpan={{
            xxs: 12,
            xs: 12,
            s: 12,
            m: 5,
            l: 5,
          }}
        >
          <Box
            style={NewLabelStyle}
            position="absolute"
            background="black"
            paddingX="s8"
            paddingY="s4"
            borderRadius={{ all: 'abs004' }}
          >
            <Text.Normal color="white" size="100" className="uppercase" weight="semibold">
              {i18n.t('product_updates.updates_with_ceo.landing.banner.benefits.new')}
            </Text.Normal>
          </Box>

          <video
            src={benefitsVideo}
            autoPlay
            muted
            loop
            style={{
              height: '100%',
              width: '100%',
              objectFit: 'cover',
              objectPosition: 'center top',
            }}
          />
        </Box>
        <Box
          paddingX="s24"
          gap="s12"
          justifyContent="center"
          paddingY="s16"
          gridColSpan={{
            xxs: 12,
            xs: 12,
            s: 12,
            m: 7,
            l: 7,
          }}
        >
          <Box flexDirection="column" alignItems="flexStart" gap="s4">
            <span className="text-md uppercase font-semibold text-f1-foreground-secondary">
              {i18n.t('product_updates.updates_with_ceo.landing.banner.benefits.factorial')}
            </span>
            <span className="text-2xl font-semibold">
              {i18n.t('product_updates.updates_with_ceo.landing.banner.benefits.newTitle')}
            </span>
          </Box>
          <Box>
            <Box gap="s4" paddingTop="s4" paddingBottom="s16">
              {benefits.map((benefit, index) => (
                <Box flexDirection="row" alignItems="flexStart" gap="s8" key={index}>
                  <Box
                    background="grey800"
                    padding="s2"
                    borderRadius={{ all: 'abs016' }}
                    alignItems="center"
                    style={{ marginTop: '4px', backgroundColor: '#25906D' }}
                  >
                    <CheckIcon color="white" />
                  </Box>
                  <Box>
                    <span className="text-lg text-f1-foreground-secondary">{benefit}</span>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box maxWidth="fit-content" alignSelf={isLargeScreen ? 'flexStart' : 'center'}>
            {canSeeUpsellButton && (
              <Button
                variant="promote"
                label={i18n.t('product_updates.banner.benefits.button')}
                onClick={handleUpsellButtonClick}
              />
            )}
          </Box>
        </Box>
      </Grid>

      {renderUpsellModal()}
    </Box>
  )
}

export default BenefitsBanner
